/**
 * O tempo lá fora: chuva, tempestade com relâmpagos, neve (com o chão, o
 * telhado e as copas a ficarem brancos), nevoeiro, ventania com folhas, e
 * pirilampos nas noites limpas de verão.
 *
 * O tempo de cada momento sai de um sorteio sobre a hora (blocos de 45 min) com
 * pesos por estação: todos os clientes vêem o mesmo tempo ao mesmo tempo, sem
 * servidor. `?clima=neve` (ou chuva, tempestade, nevoeiro, ventania, limpo) no
 * endereço força um, só nesse navegador.
 *
 * Só se vê ao ar livre: as gotas e os flocos morrem ao entrar no prédio, e a
 * neve do chão só cobre relva, caminhos e telhado. As contas são puras e estão
 * testadas; a classe só as passa para objectos do Phaser.
 */
import { isOutside, isUpstairs, LAKE, SURFACE_ROWS, TILE, UPSTAIRS_OFFSET_ROWS } from "@greenvoice/shared";
import type PhaserT from "phaser";

import { BancosDeNevoeiro } from "./officeNevoeiro";
import { Pocas } from "./officePocas";
import { eTelhado, sobreOPredio } from "./officeRoof";
import { horaDe, luzDoDia, RELVA, rectangulosDeFora } from "./officeVida";

// ── Que tempo faz ──────────────────────────────────────────────────────────

export type Tempo = "limpo" | "chuva" | "tempestade" | "neve" | "nevoeiro" | "ventania";

export const TEMPOS: readonly Tempo[] = ["limpo", "chuva", "tempestade", "neve", "nevoeiro", "ventania"];

/** Quanto dura cada tempo sorteado. */
export const BLOCO_MS = 45 * 60 * 1000;

/** Os pesos de cada tempo por mês (0 = Janeiro). */
export function pesosDoMes(mes: number): Record<Tempo, number> {
  const m = ((mes % 12) + 12) % 12;
  if (m === 11 || m <= 1) return { limpo: 35, chuva: 20, tempestade: 5, neve: 30, nevoeiro: 10, ventania: 0 };
  if (m <= 4) return { limpo: 55, chuva: 25, tempestade: 5, neve: 0, nevoeiro: 10, ventania: 5 };
  if (m <= 7) return { limpo: 75, chuva: 8, tempestade: 10, neve: 0, nevoeiro: 0, ventania: 7 };
  return { limpo: 40, chuva: 25, tempestade: 5, neve: 0, nevoeiro: 10, ventania: 20 };
}

/** Um número "ao calhas" em [0, 1) que só depende de `n` — igual em todos os clientes. */
export function sorteio(n: number): number {
  let x = Math.imul(n ^ 0x9e3779b9, 0x85ebca6b);
  x ^= x >>> 13;
  x = Math.imul(x, 0xc2b2ae35);
  x ^= x >>> 16;
  return (x >>> 0) / 2 ** 32;
}

/** O tempo que faz no instante `ms`, para um mês. */
export function tempoDoMomento(ms: number, mes: number = new Date(ms).getMonth()): Tempo {
  const pesos = pesosDoMes(mes);
  const total = TEMPOS.reduce((s, t) => s + pesos[t], 0);
  let r = sorteio(Math.floor(ms / BLOCO_MS)) * total;
  for (const t of TEMPOS) {
    r -= pesos[t];
    if (r < 0) return t;
  }
  return "limpo";
}

/** O tempo pedido no endereço (`?clima=neve`), se for um que exista. */
export function tempoPedido(search: string): Tempo | null {
  const pedido = new URLSearchParams(search).get("clima")?.trim().toLowerCase() ?? "";
  return TEMPOS.find((t) => t === pedido) ?? null;
}

const CHAVE_ESCOLHIDO = "gv:clima";

/**
 * O tempo escolhido com `/clima` no chat do escritório — só neste navegador,
 * guardado para sobreviver a um recarregar. `null` = o do sorteio.
 */
export function tempoEscolhido(): Tempo | null {
  try {
    const guardado = localStorage.getItem(CHAVE_ESCOLHIDO);
    return TEMPOS.find((t) => t === guardado) ?? null;
  } catch {
    return null;
  }
}

export function escolherTempo(tempo: Tempo | null): void {
  try {
    if (tempo) localStorage.setItem(CHAVE_ESCOLHIDO, tempo);
    else localStorage.removeItem(CHAVE_ESCOLHIDO);
  } catch {
    // Sem armazenamento (modo privado): o comando não fica.
  }
}

/** O tempo a mostrar agora: o pedido no endereço, o escolhido no chat, ou o do sorteio. */
export function tempoAgora(ms = Date.now()): Tempo {
  const search = typeof location === "undefined" ? "" : location.search;
  return tempoPedido(search) ?? tempoEscolhido() ?? tempoDoMomento(ms);
}

/** Um símbolo para o tempo, para o relógio do minimapa. */
export const SIMBOLO_DO_TEMPO: Record<Tempo, string> = {
  limpo: "",
  chuva: "🌧️",
  tempestade: "⛈️",
  neve: "❄️",
  nevoeiro: "🌫️",
  ventania: "🍃",
};

export const NOME_DO_TEMPO: Record<Tempo, string> = {
  limpo: "Céu limpo",
  chuva: "A chover",
  tempestade: "Tempestade",
  neve: "A nevar",
  nevoeiro: "Nevoeiro",
  ventania: "Ventania",
};

/** Pirilampos: noite, céu limpo, e só nos meses quentes (Maio a Outubro). */
export function haPirilampos(tempo: Tempo, noite: number, mes: number): boolean {
  return tempo === "limpo" && noite > 0.5 && mes >= 4 && mes <= 9;
}

// ── Onde se vê ─────────────────────────────────────────────────────────────

/** Este tile está ao ar livre? À superfície é o jardim; no 1.º andar, o telhado e o jardim que se vê de lá. */
export function aoAr(col: number, row: number): boolean {
  if (row < SURFACE_ROWS) return isOutside(col, row);
  if (!isUpstairs(row)) return false; // o bunker
  return eTelhado(col, row) || !sobreOPredio(col, row);
}

/**
 * Os tiles onde a neve assenta: tudo o que é jardim à superfície (o lago
 * incluído — a água recorta-se depois pelo desenho dele, não pela elipse) e o
 * telhado.
 */
export function tilesDeNeve(): { col: number; row: number }[] {
  const out: { col: number; row: number }[] = [];
  for (let row = 0; row < SURFACE_ROWS; row += 1) {
    for (let col = 0; col < 44; col += 1) if (isOutside(col, row)) out.push({ col, row });
  }
  for (let row = UPSTAIRS_OFFSET_ROWS; row < UPSTAIRS_OFFSET_ROWS + SURFACE_ROWS; row += 1) {
    for (let col = 0; col < 44; col += 1) if (eTelhado(col, row)) out.push({ col, row });
  }
  return out;
}

/**
 * Que píxeis de uma imagem de lago são ÁGUA: os azuis, e tudo o que eles
 * cercam (nenúfares, reflexos) — o que não chega à borda da imagem sem passar
 * por água. Devolve um array de 0/1, linha a linha.
 */
export function mascaraDeAgua(rgba: Uint8ClampedArray, w: number, h: number): Uint8Array {
  const agua = new Uint8Array(w * h);
  for (let i = 0; i < w * h; i += 1) {
    const r = rgba[i * 4] ?? 0;
    const g = rgba[i * 4 + 1] ?? 0;
    const b = rgba[i * 4 + 2] ?? 0;
    const a = rgba[i * 4 + 3] ?? 0;
    agua[i] = a > 0 && b > 110 && b >= r + 25 && b >= g - 6 ? 1 : 0;
  }
  // Do contorno para dentro, por tudo o que não é água: o que ficar por
  // visitar está cercado por água, e conta como lago.
  const fora = new Uint8Array(w * h);
  const pilha: number[] = [];
  for (let x = 0; x < w; x += 1) pilha.push(x, (h - 1) * w + x);
  for (let y = 0; y < h; y += 1) pilha.push(y * w, y * w + w - 1);
  while (pilha.length > 0) {
    const p = pilha.pop() ?? 0;
    if (fora[p] || agua[p]) continue;
    fora[p] = 1;
    const x = p % w;
    if (x > 0) pilha.push(p - 1);
    if (x < w - 1) pilha.push(p + 1);
    if (p >= w) pilha.push(p - w);
    if (p < w * (h - 1)) pilha.push(p + w);
  }
  for (let i = 0; i < w * h; i += 1) agua[i] = fora[i] ? 0 : 1;
  return agua;
}

/** Um píxel cinzento de pedra (as da margem do lago): pouco saturado e nem muito claro nem muito escuro. */
export function pedra(r: number, g: number, b: number, a: number): boolean {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  return a > 0 && max - min < 24 && max > 70 && max < 235;
}

/** Aproxima `atual` de `alvo` a `porSegundo` (0-1 por segundo). */
export function aproximar(atual: number, alvo: number, porSegundo: number, dtMs: number): number {
  const passo = porSegundo * (dtMs / 1000);
  return Math.abs(alvo - atual) <= passo ? alvo : atual + Math.sign(alvo - atual) * passo;
}

// ── A arte ─────────────────────────────────────────────────────────────────

const T_GOTA = "clima:gota";
const T_SALPICO = "clima:salpico";
const T_FLOCO = "clima:floco";
const T_NEVOA = "clima:nevoa";
const T_COPA = "clima:copa";
const T_PIRILAMPO = "clima:pirilampo";

function tela(
  scene: PhaserT.Scene,
  chave: string,
  w: number,
  h: number,
  pintar: (ctx: CanvasRenderingContext2D) => void,
): void {
  if (scene.textures.exists(chave)) return;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  pintar(ctx);
  scene.textures.addCanvas(chave, canvas);
}

function criarTexturas(scene: PhaserT.Scene): void {
  tela(scene, T_GOTA, 2, 16, (c) => {
    const g = c.createLinearGradient(0, 0, 0, 16);
    g.addColorStop(0, "rgba(200, 225, 255, 0)");
    g.addColorStop(1, "rgba(235, 245, 255, 1)");
    c.fillStyle = g;
    c.fillRect(0, 0, 2, 16);
  });
  tela(scene, T_SALPICO, 14, 6, (c) => {
    c.strokeStyle = "rgba(230, 242, 255, 0.95)";
    c.lineWidth = 1.5;
    c.beginPath();
    c.ellipse(7, 3, 6, 2, 0, 0, Math.PI * 2);
    c.stroke();
  });
  tela(scene, T_FLOCO, 4, 4, (c) => {
    c.fillStyle = "rgba(255, 255, 255, 0.95)";
    c.fillRect(1, 0, 2, 4);
    c.fillRect(0, 1, 4, 2);
  });
  tela(scene, T_NEVOA, 256, 128, (c) => {
    const g = c.createRadialGradient(128, 64, 4, 128, 64, 64);
    g.addColorStop(0, "rgba(235, 240, 245, 0.9)");
    g.addColorStop(1, "rgba(235, 240, 245, 0)");
    c.setTransform(2, 0, 0, 1, -128, 0);
    c.fillStyle = g;
    c.fillRect(0, 0, 256, 128);
  });
  tela(scene, T_COPA, 40, 18, (c) => {
    // Um monte de neve irregular, com a sombra azulada por baixo.
    c.fillStyle = "#b9cde0";
    for (const [x, y, rx, ry] of [
      [12, 11, 10, 6],
      [26, 11, 11, 6],
      [20, 12, 15, 5],
    ] as const) {
      c.beginPath();
      c.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2);
      c.fill();
    }
    c.fillStyle = "#f7fbff";
    for (const [x, y, rx, ry] of [
      [12, 9, 9, 6],
      [25, 8, 11, 6],
      [19, 7, 7, 5],
      [20, 10, 14, 4],
    ] as const) {
      c.beginPath();
      c.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2);
      c.fill();
    }
    c.fillRect(8, 14, 2, 3); // pingentes
    c.fillRect(29, 14, 2, 2);
  });
  tela(scene, T_PIRILAMPO, 12, 12, (c) => {
    const g = c.createRadialGradient(6, 6, 0, 6, 6, 6);
    g.addColorStop(0, "rgba(240, 255, 170, 1)");
    g.addColorStop(0.35, "rgba(190, 255, 90, 0.7)");
    g.addColorStop(1, "rgba(150, 255, 60, 0)");
    c.fillStyle = g;
    c.fillRect(0, 0, 12, 12);
  });
}

// ── A peça no mundo ────────────────────────────────────────────────────────

/** Por baixo do véu da noite (2 000 000): a noite escurece também a chuva e a neve. */
const PROF_TEMPO = 1_999_990;
const PROF_RELAMPAGO = 2_000_010;
const PROF_PIRILAMPOS = 2_000_003;
/**
 * A neve do chão: por cima da relva, do alcatrão e das riscas do parque
 * (`DEPTH_RUG`, -9000), e por BAIXO dos carros e do resto da mobília rasa
 * (`DEPTH_RUG + 2`) — senão os carros do parque ficavam brancos por baixo dela.
 */
const PROF_MANTO = -8999;

const N_NEVOAS = 7;
const N_PIRILAMPOS = 26;

const porCena = new WeakMap<PhaserT.Scene, Clima>();

export function climaDaCena(scene: PhaserT.Scene): Clima | undefined {
  return porCena.get(scene);
}

interface Copa {
  planta: PhaserT.GameObjects.Image;
  neve: PhaserT.GameObjects.Image;
  /** A que fracção da altura da planta assenta, a contar do pé. */
  altura: number;
}

export class Clima {
  private readonly cam: PhaserT.Cameras.Scene2D.Camera;
  private readonly chuva: PhaserT.GameObjects.Particles.ParticleEmitter;
  private readonly salpicos: PhaserT.GameObjects.Particles.ParticleEmitter;
  private readonly neve: PhaserT.GameObjects.Particles.ParticleEmitter;
  private readonly folhas: PhaserT.GameObjects.Particles.ParticleEmitter;
  private readonly manto: PhaserT.GameObjects.Image | PhaserT.GameObjects.Graphics;
  private readonly mantoDoTelhado: PhaserT.GameObjects.Graphics;
  private readonly copas: Copa[];
  private readonly nevoas: PhaserT.GameObjects.Image[];
  private readonly molhado: PhaserT.GameObjects.Rectangle[];
  private readonly relampago: PhaserT.GameObjects.Rectangle;
  private readonly pirilampos: { img: PhaserT.GameObjects.Image; x: number; y: number }[];
  private readonly pocas: Pocas;
  private readonly bancos: BancosDeNevoeiro;

  /** Intensidades actuais (0-1), a caminho das do tempo que faz. */
  private q = { chuva: 0, forte: 0, neve: 0, manto: 0, nevoa: 0, vento: 0, pirilampos: 0 };
  private tempo: Tempo = "limpo";
  private proximoRelampago = 0;
  private relampagoDesde = -1;
  private ultimaConsulta = -Infinity;

  /**
   * `plantas` são as árvores e os arbustos do jardim (levam neve em cima);
   * `folha` a textura das folhas da ventania; `lago` a do lago desenhado, que é
   * por onde a neve do chão se recorta.
   */
  constructor(scene: PhaserT.Scene, plantas: readonly PhaserT.GameObjects.Image[], folha: string, lago: string) {
    criarTexturas(scene);
    this.cam = scene.cameras.main;
    const cam = this.cam;

    // Nascem na zona que a câmara vê (um pouco acima, para já virem a cair) e
    // morrem ao entrar num sítio com tecto.
    const zonaDaVista = (acima: number, margem: number) => ({
      getRandomPoint: (p: PhaserT.Types.Math.Vector2Like) => {
        const v = cam.worldView;
        p.x = v.x - margem + Math.random() * (v.width + margem * 2);
        p.y = v.y - acima + Math.random() * (v.height + acima);
      },
    });
    const soAoAr = {
      type: "onEnter",
      source: { contains: (x: number, y: number) => !aoAr(Math.floor(x / TILE), Math.floor(y / TILE)) },
    };

    this.chuva = scene.add
      .particles(0, 0, T_GOTA, {
        emitZone: { type: "random", source: zonaDaVista(420, 120) },
        deathZone: soAoAr,
        lifespan: { min: 450, max: 1050 },
        speedY: { min: 560, max: 680 },
        speedX: { min: -90, max: -60 },
        rotate: 8,
        alpha: { start: 0.9, end: 0.5 },
        frequency: 16,
        quantity: 1,
        emitting: false,
      })
      .setDepth(PROF_TEMPO);

    this.salpicos = scene.add
      .particles(0, 0, T_SALPICO, {
        emitZone: { type: "random", source: zonaDaVista(0, 0) },
        deathZone: soAoAr,
        lifespan: 320,
        scale: { start: 0.5, end: 1.5 },
        alpha: { start: 0.7, end: 0 },
        frequency: 30,
        quantity: 1,
        emitting: false,
      })
      .setDepth(PROF_TEMPO - 1);

    this.neve = scene.add
      .particles(0, 0, T_FLOCO, {
        emitZone: { type: "random", source: zonaDaVista(260, 80) },
        deathZone: soAoAr,
        lifespan: { min: 6000, max: 9500 },
        speedY: { min: 28, max: 58 },
        speedX: { min: -22, max: 14 },
        scale: { min: 0.6, max: 1.3 },
        alpha: { start: 0.95, end: 0.7 },
        frequency: 70,
        quantity: 1,
        emitting: false,
      })
      .setDepth(PROF_TEMPO);

    this.folhas = scene.add
      .particles(0, 0, folha, {
        emitZone: {
          type: "random",
          source: {
            getRandomPoint: (p: PhaserT.Types.Math.Vector2Like) => {
              const v = cam.worldView;
              p.x = v.x - 60 - Math.random() * 120;
              p.y = v.y + Math.random() * v.height;
            },
          },
        },
        deathZone: soAoAr,
        lifespan: { min: 5000, max: 8000 },
        speedX: { min: 140, max: 260 },
        speedY: { min: -25, max: 45 },
        rotate: { start: 0, end: 720 },
        scale: { min: 0.8, max: 1.4 },
        alpha: { start: 1, end: 0.6 },
        frequency: 140,
        quantity: 1,
        emitting: false,
      })
      .setDepth(PROF_TEMPO);

    // O manto de neve do jardim: uma imagem só, com a água do lago recortada
    // pelo próprio desenho — as margens ficam nevadas e a água não.
    const tiles = tilesDeNeve();
    this.manto = this.pintarManto(
      scene,
      tiles.filter((t) => t.row < SURFACE_ROWS),
      lago,
    );
    this.manto.setDepth(PROF_MANTO).setAlpha(0);
    // O do telhado é rectangular como ele: quadrados chegam.
    this.mantoDoTelhado = scene.add.graphics().setDepth(PROF_MANTO).setAlpha(0);
    this.mantoDoTelhado.fillStyle(0xeef3f9, 0.94);
    for (const { col, row } of tiles.filter((t) => t.row >= SURFACE_ROWS)) {
      this.mantoDoTelhado.fillRect(col * TILE, row * TILE, TILE, TILE);
    }

    // Neve em cima das árvores e dos arbustos, a acompanhar o vento.
    this.copas = plantas
      .filter((p) => p.displayHeight >= 16)
      .flatMap((planta) => {
        const alta = planta.displayHeight > 48;
        const camadas = alta
          ? [
              { altura: 0.9, largura: 0.5 },
              { altura: 0.62, largura: 0.78 },
            ]
          : [{ altura: 0.82, largura: 0.7 }];
        return camadas.map(({ altura, largura }) => ({
          planta,
          altura,
          neve: scene.add
            .image(planta.x, planta.y, T_COPA)
            .setOrigin(0.5, 0.6)
            .setAlpha(0)
            .setVisible(false)
            .setDisplaySize(planta.displayWidth * largura, planta.displayWidth * largura * 0.42),
        }));
      });

    // O nevoeiro só se vê lá fora: recorta-se pelos mesmos rectângulos do véu.
    const recorte = scene.make.graphics({}, false).fillStyle(0xffffff);
    for (const [x, y, w, h] of rectangulosDeFora()) recorte.fillRect(x, y, w, h);
    const mascara = recorte.createGeometryMask();
    this.nevoas = Array.from({ length: N_NEVOAS }, (_, i) =>
      scene.add
        .image(0, 0, T_NEVOA)
        .setScale(2.4 + (i % 3) * 0.6, 1.8 + (i % 2) * 0.5)
        .setAlpha(0)
        .setDepth(PROF_TEMPO - 2)
        .setMask(mascara),
    );

    // E os bancos densos, que andam pelo jardim e escondem quem passa.
    this.bancos = new BancosDeNevoeiro(scene, PROF_TEMPO - 2, mascara);

    this.pocas = new Pocas(scene, T_SALPICO);

    // O chão molhado e o céu carregado: um véu nos mesmos sítios de fora.
    this.molhado = rectangulosDeFora().map(([x, y, w, h]) =>
      scene.add
        .rectangle(x, y, w, h, 0x0e1826, 0)
        .setOrigin(0, 0)
        .setDepth(PROF_TEMPO - 3),
    );

    this.relampago = scene.add.rectangle(0, 0, 1, 1, 0xeaf2ff, 0).setOrigin(0, 0).setDepth(PROF_RELAMPAGO);

    this.pirilampos = Array.from({ length: N_PIRILAMPOS }, (_, i) => {
      const r = RELVA[Math.floor(sorteio(i * 977 + 3) * RELVA.length)] ?? { col: 5, row: 25 };
      const x = (r.col + sorteio(i * 31)) * TILE;
      const y = (r.row + sorteio(i * 57)) * TILE;
      return {
        x,
        y,
        // 1 = BlendModes.ADD: a luz soma-se ao que está por baixo.
        img: scene.add.image(x, y, T_PIRILAMPO).setBlendMode(1).setAlpha(0).setDepth(PROF_PIRILAMPOS),
      };
    });

    porCena.set(scene, this);
  }

  /** O tempo que se está a mostrar. */
  get agora(): Tempo {
    return this.tempo;
  }

  /** Quanta neve há no chão agora (0-1) — é o que deixa apanhar bolas de neve. */
  get neveNoChao(): number {
    return this.q.manto;
  }

  /** `pessoas`: os pés de quem está à vista, para os salpicos nas poças. */
  update(agora: number, dtMs: number, pessoas: readonly { id: string; x: number; y: number }[] = []): void {
    if (agora - this.ultimaConsulta > 400) {
      this.ultimaConsulta = agora;
      this.tempo = tempoAgora();
    }
    const t = this.tempo;
    const data = new Date();
    const noite = luzDoDia(horaDe(data)).noite;

    // As intensidades vão devagar para as do tempo que faz: a chuva começa e
    // acaba em segundos, a neve do chão demora a juntar e mais ainda a derreter.
    const q = this.q;
    q.chuva = aproximar(q.chuva, t === "chuva" || t === "tempestade" ? 1 : 0, 1 / 8, dtMs);
    q.forte = aproximar(q.forte, t === "tempestade" ? 1 : 0, 1 / 8, dtMs);
    q.neve = aproximar(q.neve, t === "neve" ? 1 : 0, 1 / 8, dtMs);
    // A juntar demora (uma nevada não cobre o chão num instante); a derreter é
    // bem mais rápido do que era — trocar de tempo e ficar dois minutos com neve
    // no chão lia-se como coisa presa.
    q.manto = aproximar(q.manto, t === "neve" ? 1 : 0, t === "neve" ? 1 / 45 : 1 / 30, dtMs);
    q.nevoa = aproximar(q.nevoa, t === "nevoeiro" ? 1 : 0, 1 / 10, dtMs);
    q.vento = aproximar(q.vento, t === "ventania" ? 1 : 0, 1 / 10, dtMs);
    q.pirilampos = aproximar(q.pirilampos, haPirilampos(t, noite, data.getMonth()) ? 1 : 0, 1 / 10, dtMs);

    const v = this.cam.worldView;
    // Sem céu por cima (o bunker), não há tempo nenhum.
    const linhaDoMeio = Math.floor((v.y + v.height / 2) / TILE);
    const ceu = linhaDoMeio >= SURFACE_ROWS && !isUpstairs(linhaDoMeio) ? 0 : 1;

    this.ajustar(this.chuva, q.chuva * ceu, 6 + 8 * q.forte);
    this.ajustar(this.salpicos, q.chuva * ceu, 3 + 3 * q.forte);
    this.ajustar(this.neve, q.neve * ceu, 3);
    this.ajustar(this.folhas, q.vento * ceu, 2);

    this.manto.setAlpha(q.manto).setVisible(q.manto > 0.005);
    this.mantoDoTelhado.setAlpha(q.manto).setVisible(q.manto > 0.005);
    for (const c of this.copas) {
      const mostrar = q.manto > 0.005;
      c.neve.setVisible(mostrar);
      if (!mostrar) continue;
      const rot = c.planta.rotation;
      const d = c.planta.displayHeight * c.altura;
      c.neve
        .setPosition(c.planta.x + Math.sin(rot) * d, c.planta.y - Math.cos(rot) * d)
        .setRotation(rot)
        .setAlpha(q.manto)
        .setDepth(c.planta.depth + 0.5);
    }

    this.nevoas.forEach((n, i) => {
      const largura = v.width + 700;
      const x = v.x - 350 + ((((i * 373 + agora * (0.012 + i * 0.002)) % largura) + largura) % largura);
      const y = v.y + ((i + 0.5) / N_NEVOAS) * v.height + Math.sin(agora / 5000 + i) * 20;
      n.setPosition(x, y)
        .setAlpha(q.nevoa * 0.35)
        .setVisible(q.nevoa > 0.005);
    });

    this.bancos.update(q.nevoa);
    this.pocas.update(agora, dtMs, q.chuva * ceu, pessoas);

    const carregado = Math.max(q.chuva * 0.22, q.forte * 0.32, q.nevoa * 0.14);
    for (const r of this.molhado) r.setFillStyle(q.nevoa > q.chuva ? 0xc9d2da : 0x0e1826, carregado);

    this.relampagos(agora, q.forte * ceu, v);

    this.pirilampos.forEach((f, i) => {
      if (q.pirilampos <= 0.005) {
        f.img.setVisible(false);
        return;
      }
      const x = f.x + Math.sin(agora / 1700 + i) * 16 + Math.sin(agora / 530 + i * 3) * 3;
      const y = f.y + Math.cos(agora / 2300 + i * 1.7) * 10;
      const pisca = 0.35 + 0.65 * Math.max(0, Math.sin(agora / 620 + i * 2.3));
      f.img
        .setVisible(true)
        .setPosition(x, y)
        .setAlpha(q.pirilampos * pisca);
    });
  }

  private pintarManto(
    scene: PhaserT.Scene,
    tiles: readonly { col: number; row: number }[],
    lago: string,
  ): PhaserT.GameObjects.Image | PhaserT.GameObjects.Graphics {
    const canvas = document.createElement("canvas");
    canvas.width = 44 * TILE;
    canvas.height = SURFACE_ROWS * TILE;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) {
      // Sem canvas (testes): quadrados, como o telhado.
      const g = scene.add.graphics().fillStyle(0xeef3f9, 0.94);
      for (const { col, row } of tiles) g.fillRect(col * TILE, row * TILE, TILE, TILE);
      return g;
    }
    ctx.fillStyle = "rgba(240, 245, 251, 0.95)";
    for (const { col, row } of tiles) ctx.fillRect(col * TILE, row * TILE, TILE, TILE);
    // Um pouco de relevo: montinhos azulados aqui e ali.
    ctx.fillStyle = "rgba(196, 214, 232, 0.8)";
    for (const { col, row } of tiles) {
      const r = sorteio(col * 131 + row * 7);
      if (r > 0.55) ctx.fillRect(col * TILE + Math.floor(r * 22), row * TILE + Math.floor(r * 17) + 4, 6, 2);
    }
    if (scene.textures.exists(lago)) {
      const fonte = scene.textures.get(lago).getSourceImage();
      const lw = fonte.width;
      const lh = fonte.height;
      const tmp = document.createElement("canvas");
      tmp.width = lw;
      tmp.height = lh;
      const tctx = tmp.getContext("2d", { willReadFrequently: true });
      if (tctx && (fonte instanceof HTMLCanvasElement || fonte instanceof HTMLImageElement)) {
        tctx.drawImage(fonte, 0, 0);
        const agua = mascaraDeAgua(tctx.getImageData(0, 0, lw, lh).data, lw, lh);
        const ox = Math.round((LAKE.cx - LAKE.rx) * TILE);
        const oy = Math.round((LAKE.cy - LAKE.ry) * TILE);
        const neve = ctx.getImageData(ox, oy, lw, lh);
        const lagoPx = tctx.getImageData(0, 0, lw, lh).data;
        for (let i = 0; i < lw * lh; i += 1) {
          if (agua[i]) {
            neve.data[i * 4 + 3] = 0;
            continue;
          }
          if (pedra(lagoPx[i * 4] ?? 0, lagoPx[i * 4 + 1] ?? 0, lagoPx[i * 4 + 2] ?? 0, lagoPx[i * 4 + 3] ?? 0)) {
            neve.data[i * 4 + 3] = Math.min(neve.data[i * 4 + 3] ?? 0, 70);
          }
        }
        ctx.putImageData(neve, ox, oy);
      }
    }
    const chave = "clima:manto";
    if (!scene.textures.exists(chave)) scene.textures.addCanvas(chave, canvas);
    return scene.add.image(0, 0, chave).setOrigin(0, 0);
  }

  /** Liga, desliga e dá quantidade a um emissor conforme a intensidade. */
  private ajustar(e: PhaserT.GameObjects.Particles.ParticleEmitter, q: number, maximo: number): void {
    if (q < 0.02) {
      if (e.emitting) e.stop();
      return;
    }
    e.quantity = Math.max(1, Math.round(q * maximo));
    if (!e.emitting) e.start();
  }

  private relampagos(agora: number, forte: number, v: PhaserT.Geom.Rectangle): void {
    this.relampago.setPosition(v.x, v.y).setScale(v.width, v.height);
    if (forte < 0.5) {
      this.relampago.setAlpha(0);
      return;
    }
    if (this.proximoRelampago === 0) this.proximoRelampago = agora + 4000 + Math.random() * 8000;
    if (agora >= this.proximoRelampago) {
      this.relampagoDesde = agora;
      this.proximoRelampago = agora + 7000 + Math.random() * 14000;
    }
    const t = agora - this.relampagoDesde;
    // Dois clarões seguidos, o segundo mais fraco — é o que se vê numa trovoada.
    const clarao =
      this.relampagoDesde < 0
        ? 0
        : t < 90
          ? 0.55
          : t < 160
            ? 0.1
            : t < 240
              ? 0.35
              : Math.max(0, 0.35 - (t - 240) / 600);
    this.relampago.setAlpha(clarao * forte);
  }
}
